import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  ContentDialog,
  ContentDialogInit,
} from "../MDDForm/components/ContentDialog.jsx";
import { backendGet } from "services/dataService.js";
import { useSnackbar } from "notistack";
import { useClaimCommunicationActions } from "../../hooks/useClaimCommunicationActions.jsx";
import ForwardCommunicationForm from "./ForwardCommunicationForm.jsx";

function ForwardCommunicationModal({
  open,
  onClose,
  kommunikation_id,
  schaden_id,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [communication, setCommunication] = useState(null);

  const { openCD, setOpenCD, tca, setTca, CDActions, setCDActions } =
    ContentDialogInit();

  const { forwardCommunication } = useClaimCommunicationActions();

  // fetch data
  useEffect(() => {
    if (!open || !kommunikation_id) {
      setCommunication(null);
      return;
    }

    const fetchKommunikation = async () => {
      await backendGet(
        `data/kommunikations/${schaden_id}/${kommunikation_id}`,
        {
          options: {
            params: {
              include: ["createdBy", "anlagen"],
            },
          },
          label: "Kommunikation",
          setResult: (res) => setCommunication(res || {}),
          setIsLoading,
        }
      );
    };

    fetchKommunikation();
    // eslint-disable-next-line
  }, [open, schaden_id, kommunikation_id, enqueueSnackbar]);

  const attachmentOptions = useMemo(() => {
    return (communication?.anlagen || []).map((a) => ({
      id: a.id,
      label: a.bezeichnung || a.filename,
    }));
  }, [communication]);

  const defaultValues = useMemo(() => {
    if (!communication) return null;

    return {
      subject: communication.betreff ? `WG: ${communication.betreff}` : "WG:",
      body: "",
      forward_to: [],
      forward_cc: [],
      forward_bcc: [],
      attachments: attachmentOptions.map((a) => a.id),
    };
  }, [communication, attachmentOptions]);

  // dialog init
  useEffect(() => {
    if (!open || !kommunikation_id) {
      setOpenCD(false);
      setCDActions(null);
      return;
    }

    setOpenCD(true);
    setCDActions(null);
    setTca({
      title: "Kommunikation weiterleiten",
      maxWidth: "md",
      content:
        isLoading || !communication ? (
          <div>Daten werden geladen...</div>
        ) : (
          <ForwardCommunicationForm
            key={communication?.id}
            defaultValues={defaultValues}
            attachmentOptions={attachmentOptions}
            communication={communication}
            onSubmit={async (data) => {
              const result = await forwardCommunication(
                communication.id,
                { ...data },
                {
                  onSuccess: () => {
                    enqueueSnackbar("Kommunikation weitergeleitet", {
                      variant: "success",
                    });
                    onClose();
                  },
                }
              );
              return result !== null;
            }}
            onCancel={onClose}
          />
        ),
      actions: null,
    });
  }, [
    open,
    attachmentOptions,
    communication,
    defaultValues,
    enqueueSnackbar,
    forwardCommunication,
    isLoading,
    onClose,
  ]);

  return (
    <ContentDialog
      openCD={openCD}
      setOpenCD={setOpenCD}
      tca={tca}
      CDActions={CDActions}
      onClose={onClose}
    />
  );
}

ForwardCommunicationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  kommunikation_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  schaden_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ForwardCommunicationModal;
